import { useCallback, useEffect, useMemo, useState } from "react";
import { isValidImage } from "src/helpers/image";
import { promiseStatuses } from "src/constants/constants";

const useReachableImages = (urls) => {
  const [unreachableUrls, setUnreachableUrls] = useState([]);
  const [isChecking, setIsChecking] = useState(false);

  const urlsKey = Array.isArray(urls) ? urls.join("|") : "";

  useEffect(() => {
    if (!Array.isArray(urls) || urls.length === 0) {
      setUnreachableUrls([]);
      setIsChecking(false);
      return undefined;
    }

    let isCancelled = false;
    setIsChecking(true);

    Promise.allSettled(urls.map((url) => isValidImage(url)))
      .then((results) => {
        if (isCancelled) {
          return;
        }
        const broken = results.reduce((acc, result, index) => {
          if (result.status === promiseStatuses.REJECTED) {
            return [...acc, urls[index]];
          }
          return acc;
        }, []);
        setUnreachableUrls(broken);
      })
      .catch(() => {
        if (!isCancelled) {
          setUnreachableUrls([]);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsChecking(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [urlsKey]);

  const markUnreachable = useCallback((url) => {
    if (!url) {
      return;
    }
    setUnreachableUrls((current) => {
      if (current.includes(url)) {
        return current;
      }
      return [...current, url];
    });
  }, []);

  const reachableUrls = useMemo(() => {
    if (!Array.isArray(urls)) {
      return [];
    }
    if (unreachableUrls.length === 0) {
      return urls;
    }
    return urls.filter((url) => !unreachableUrls.includes(url));
  }, [urlsKey, unreachableUrls]);

  return { reachableUrls, unreachableUrls, isChecking, markUnreachable };
};

export default useReachableImages;
